import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './FeedbackButton.css';
import useServerStatus from '../hooks/useServerStatus';

const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const serverStatus = useServerStatus();
  const serverOffline = serverStatus === 'offline';

  const submit = async () => {
    const trimmed = message.trim();
    if (!trimmed) {
      setStatus({
        type: 'error',
        message: 'Please share a quick note so we know how to improve.',
      });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/feedback', { message: trimmed });
      setStatus({
        type: 'success',
        message: 'Thanks for your feedback! We appreciate you.',
      });
      setMessage('');
      setOpen(false);
    } catch (err) {
      setStatus({
        type: 'error',
        message: 'Failed to submit feedback. Please try again in a moment.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!status) return undefined;

    const timeout = setTimeout(() => setStatus(null), 5000);
    return () => clearTimeout(timeout);
  }, [status]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const toggleOpen = () => {
    if (serverOffline) {
      setStatus({
        type: 'error',
        message: 'The server is currently offline. Please try again soon.',
      });
      return;
    }
    setOpen((prev) => !prev);
  };

  const closeForm = () => setOpen(false);

  return (
    <div className="feedback-float">
      {open && <div className="feedback-overlay" onClick={closeForm} aria-hidden="true" />}
      <div className={`feedback-widget${open ? ' feedback-widget--open' : ''}`}>
        {open && (
          <div
            className="feedback-form"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
          >
            <div className="feedback-header">
              <h3 id="feedback-title">Share your thoughts</h3>
              <p>
                Your ideas help us polish Polaris. Tell us what’s working and what could shine
                brighter.
              </p>
            </div>
            <textarea
              className="feedback-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Drop a quick note or suggestion..."
            />
            <div className="feedback-actions">
              <button
                type="button"
                className="feedback-btn"
                onClick={submit}
                disabled={submitting}
              >
                {submitting ? 'Sending…' : 'Send feedback'}
              </button>
              <button
                type="button"
                className="feedback-btn feedback-btn--ghost"
                onClick={closeForm}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        <button
          type="button"
          className={`feedback-toggle${open ? ' feedback-toggle--active' : ''}${
            serverOffline ? ' feedback-toggle--disabled' : ''
          }`}
          onClick={toggleOpen}
        >
          <span className="feedback-icon" aria-hidden="true">
            💬
          </span>
          <span className="feedback-label">{open ? 'Close' : 'Feedback'}</span>
        </button>
      </div>
      {status && (
        <div className={`feedback-toast feedback-toast--${status.type}`} role="status">
          {status.message}
        </div>
      )}
    </div>
  );
};

export default FeedbackButton;
